import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button, IconButton } from "@mui/material";
import { Add, InfoOutlined, ModeEditOutlineTwoTone } from "@mui/icons-material";

import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import AppTable from "components/Shared/AppTable";
import CustomTabs from "components/Shared/CustomTabs";
import HighlightedText from "components/Shared/HighlightedText";

import { useLazyListDataIntegrationQuery } from "store/services/dataIntegrationApi";

import { useAppMeta, useTablePagination } from "hooks";
import TableFilter from "components/Shared/AppTable/TableFilter";
import { localizeDateShort } from "utils/dateFormats";


const DataIntegrationListScreen = () => {
  const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
    useTablePagination();

  const [searchParams] = useSearchParams();

  const activeTab = searchParams.get("tab") || "Active";
  const keyword = searchParams.get("search") || "";

  const breadcrumbs = [{ title: " Data Integration" }];



  const dataIntegrationListTabs = [
    { label: "Active", value: "Active" },
    { label: "Inactive", value: "Inactive" },
  ];

  const columns = [
    {
      name: "Integration Id",
      selector: (row) => row.integrationId,
      cell: (row) => (
        <Link
          title={row?.integrationId}
          to={`/app/crm/dataIntegration/${row?._id}/detail`}
          className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
        >
          {row?.integrationId ? (
            <HighlightedText text={row?.integrationId} highlight={keyword} />
          ) : (
            "N/A"
          )}
        </Link>
      ),
    },
    {
      name: "Document Type",
      selector: (row) => row.documentType,
      cell: (row) => (
        <Link
          title={row?.documentType}
          to={`/app/crm/dataIntegration/${row?._id}/detail`}
          className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
        >
          {row?.documentType ? (
            <HighlightedText text={row?.documentType} highlight={keyword} />
          ) : (
            "N/A"
          )}
        </Link>
      ),
    },
    {
      name: "Status Update",
      selector: (row) => row.statusUpdate,
      cell: (row) => (
        <Link
          title={row?.statusUpdate}
          to={`/app/crm/dataIntegration/${row?._id}/detail`}
          className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
        >
          {row?.statusUpdate ? (
            <HighlightedText text={row?.statusUpdate} highlight={keyword} />
          ) : (
            "N/A"
          )}
        </Link>
      ),
    },
    {
      name: "Status",
      selector: (row) => row.status,
      cell: (row) => (
        <Link
          title={row?.status}
          to={`/app/crm/dataIntegration/${row?._id}/detail`}
          className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
        >
          {row?.status ? (
            <HighlightedText text={row?.status} highlight={keyword} />
          ) : (
            "N/A"
          )}
        </Link>
      ),
    },
    
    
    {
      name: "Timestamp",
      selector: (row) => row?.timestamp,
      cell: (row) => (
        <a
          href={`/app/crm/dataIntegration/${row?._id}/detail`}
          className="hover:text-blue-600 hover:underline dark:hover:text-blue-300"
        >
          {row?.timestamp ? (
            <HighlightedText 
              text={localizeDateShort(row?.timestamp)} 
              highlight={keyword} 
            />
          ) : (
            "N/A"
          )}
        </a>
      ),
    },
    

    {
      name: "Actions",
      cell: (row) => (
        <>
          <Link to={`/app/crm/dataIntegration/${row._id}/detail`}>
            <IconButton size="small" aria-label="View Data Integration" color="primary">
              <InfoOutlined />
            </IconButton>
          </Link>

          <Link to={`/app/crm/dataIntegration/${row._id}/edit`}>
            <IconButton size="small" aria-label="Edit Data Integration">
              <ModeEditOutlineTwoTone />
            </IconButton>
          </Link>
        </>
      ),
      ignoreRowClick: true,
      right: true,
    },
  ];

  const [fetchDataIntegrations, { data: dataIntegrationRes, isLoading, isFetching, error }] =
  useLazyListDataIntegrationQuery();
  
    React.useEffect(() => {
      fetchDataIntegrations({
        active: activeTab !== "Inactive",
        page: currentPage,
        limit: perPage,
        ...(keyword && { keyword }),
      });
    }, [activeTab, currentPage, fetchDataIntegrations, keyword, perPage]);

  useAppMeta(`${activeTab} DataIntegrations`);

  return (
    <BreadcrumbLayout breadcrumbs={breadcrumbs} headerActions={<PageActions />}>
      <BreadcrumbLayout.Error error={error} />

      <BreadcrumbLayout.Paper>
        <CustomTabs tabs={dataIntegrationListTabs} activeTab={activeTab} />

        <TableFilter
          searchLabel="Integration ID"
          disabled={isLoading || isFetching}
        >
          <div className="font-medium"> {activeTab} Data Integrations </div>
        </TableFilter>

        <AppTable
          columns={columns}
          data={dataIntegrationRes?.data ?? []}
          progressPending={isLoading || isFetching}
          paginationServer
          paginationPerPage={perPage}
          paginationTotalRows={dataIntegrationRes?.totalItems ?? 0}
          paginationDefaultPage={currentPage}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
        />
      </BreadcrumbLayout.Paper>
    </BreadcrumbLayout>
  );
};

export default DataIntegrationListScreen;

const PageActions = () => {
  return (
    <>
      <Link to="/app/crm/dataIntegration/create">
        <Button startIcon={<Add />} variant="contained">
          New Data Integration
        </Button>
      </Link>
    </>
  );
};
